<template>
    <div class="quiz-container">
      <h2 class="quiz-title">{{ quiz.title }}</h2>
      <p class="question-counter">Question {{ currentQuestionIndex + 1 }} of {{ quiz.questions.length }}</p>
      <div class="question-box">
        <h3 class="question">{{ quiz.questions[currentQuestionIndex].question }}</h3>
        <ul class="options-list">
          <li
            v-for="(option, index) in quiz.questions[currentQuestionIndex].options"
            :key="index"
            @click="selectOption(index)"
            :class="{ 'selected': selectedOptionIndex === index, 'correct': isAnswerRevealed && index === quiz.questions[currentQuestionIndex].correctAnswer, 'incorrect': isAnswerRevealed && selectedOptionIndex === index && index !== quiz.questions[currentQuestionIndex].correctAnswer }"
            class="option"
          >
            {{ option }}
            <span v-if="showFeedback && currentQuestion.correctAnswer === index" class="feedback-icon correct">✓</span>
        <span v-if="showFeedback && selectedOption === index && currentQuestion.correctAnswer !== index" class="feedback-icon incorrect">✗</span>
          </li>
        </ul>
      </div>
      <div v-if="isAnswerRevealed" class="feedback">
        <p :class="{ 'feedback-correct': isCorrect, 'feedback-incorrect': !isCorrect }">
          {{ feedbackMessage }}
        </p>
        
      </div>
      <div class="navigation-buttons">
        <button @click="previousQuestion" :disabled="currentQuestionIndex === 0" class="action-button">Back</button>
        <button @click="nextQuestion" :disabled="currentQuestionIndex === quiz.questions.length - 1 || !isAnswerRevealed" class="action-button">Next</button>
      </div>
      <div v-if="currentQuestionIndex === quiz.questions.length - 1 && isAnswerRevealed" class="results-summary">
        <h4>Quiz Summary</h4>
        <p>Correct answers: {{ correctAnswersCount }}</p>
        <p>Incorrect answers: {{ incorrectAnswersCount }}</p>
        <button @click="retakeQuiz" class="action-button">Retake Quiz</button>
        <button @click="goToNextQuiz" class="action-button next-quiz-button">Next Quiz</button>
      </div>
    </div><br><br><br><br><br><br>
  </template>
  
  <script>
import { ref } from 'vue';

export default {
  setup() {
  
    const correctAnswerSound = new Audio('https://assets.codepen.io/11151897/stock-market-bell.mp3');
    const quiz = ref({
      title: "Understanding Market-Linked GICs",
      questions: [
        {
          question: "What does GIC stand for?",
          options: [
            "Guaranteed Investment Certificate",
            "General Interest Component",
            "Growth Investment Curve",
            "Guaranteed Income Corporation"
          ],
          correctAnswer: 0
        },
        {
          question: "What is a market-linked GIC?",
          options: [
            "A fixed interest investment",
            "An investment with returns linked to stock market performance",
            "A government-issued bond",
            "A high-risk stock option"
          ],
          correctAnswer: 1
        },
        {
          question: "What is the main advantage of investing in a market-linked GIC?",
          options: [
            "High returns comparable to stocks with guaranteed principal",
            "Guaranteed high interest rate",
            "Flexibility in withdrawal at any time",
            "Direct exposure to international markets"
          ],
          correctAnswer: 0
        },
        {
          question: "What kind of protection do market-linked GICs offer?",
          options: [
            "Protection against market volatility with guaranteed principal return",
            "Insurance against inflation",
            "Guaranteed dividend payments",
            "Protection against interest rate fluctuations"
          ],
          correctAnswer: 0
        },
        {
          question: "How do returns on market-linked GICs compare to traditional GICs?",
          options: [
            "They are higher, as they are tied to market performance",
            "They are the same, as both offer fixed interest rates",
            "They can be lower if the market performs poorly",
            "There are no returns; the principal is simply protected"
          ],
          correctAnswer: 0
        },
        {
          question: "What is a potential drawback of investing in market-linked GICs?",
          options: [
            "The principal is at risk if the market declines",
            "Returns are capped, limiting the maximum profit",
            "They offer lower returns than savings accounts",
            "There is a high fee for early withdrawal"
          ],
          correctAnswer: 1
        },
        {
          question: "Who might find market-linked GICs especially appealing?",
          options: [
            "Investors looking for high risk and high return",
            "Investors seeking stock market exposure without risking the principal",
            "Investors interested in short-term trading",
            "Investors preferring consistent dividend income"
          ],
          correctAnswer: 1
        },
        {
          question: "How does the return guarantee work for a market-linked GIC?",
          options: [
            "The return is guaranteed to match the stock market's performance",
            "Only the principal amount is guaranteed, not the returns",
            "Both principal and a minimum interest rate are guaranteed",
            "Returns are guaranteed to exceed those of traditional GICs"
          ],
          correctAnswer: 1
        },
        {
          question: "Can you lose money on a market-linked GIC?",
          options: [
            "Yes, if the stock market crashes",
            "No, the principal is guaranteed even if the market performs poorly",
            "Yes, due to high management fees",
            "No, because it offers a fixed interest rate"
          ],
          correctAnswer: 1
        },
        {
          question: "What is the typical term length for a market-linked GIC?",
          options: [
            "1 to 2 years",
            "3 to 5 years",
            "5 to 10 years",
            "Varies widely based on market conditions"
          ],
          correctAnswer: 1
        }
      ]
    });
    const currentQuestionIndex = ref(0);
    const correctAnswersCount = ref(0);
    const incorrectAnswersCount = ref(0);
    const selectedOptionIndex = ref(-1);
    const isAnswerRevealed = ref(false);
    const isCorrect = ref(false);
    const feedbackMessage = ref("");

    const selectOption = (index) => {
      if (isAnswerRevealed.value) return;
      selectedOptionIndex.value = index;
      isAnswerRevealed.value = true;

      if (index === quiz.value.questions[currentQuestionIndex.value].correctAnswer) {
        isCorrect.value = true;
        correctAnswersCount.value++;
        feedbackMessage.value = "Correct!";
        correctAnswerSound.play().catch(error => console.error("Audio play failed", error));
      } else {
        isCorrect.value = false;
        incorrectAnswersCount.value++;
        feedbackMessage.value = "Incorrect.";
      }
    };

    const nextQuestion = () => {
      if (currentQuestionIndex.value < quiz.value.questions.length - 1) {
        currentQuestionIndex.value++;
        resetState();
      }
    };

    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        currentQuestionIndex.value--;
        resetState();
      }
    };

    const retakeQuiz = () => {
      currentQuestionIndex.value = 0;
      correctAnswersCount.value = 0;
      incorrectAnswersCount.value = 0;
      resetState();
    };

    const resetState = () => {
      selectedOptionIndex.value = -1;
      isAnswerRevealed.value = false;
      isCorrect.value = false;
      feedbackMessage.value = "";
    };

    return {
      quiz,
      currentQuestionIndex,
      correctAnswersCount,
      incorrectAnswersCount,
      selectedOptionIndex,
      isAnswerRevealed,
      isCorrect,
      feedbackMessage,
      selectOption,
      nextQuestion,
      previousQuestion,
      retakeQuiz,
    };
  },
};
</script>


<style scoped>
.quiz-container {
  color: #FFF;
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  margin: auto;
  background: linear-gradient(135deg, #6D5BBA 0%, #8D58BF 100%);
}


.feedback-correct, .feedback-incorrect {
  padding: 5px 10px; /* Adds some padding around the text */
  border-radius: 15px; /* Rounds the corners for a bubble-like appearance */
  display: inline-block; /* Makes the background wrap the text */
  color: #FFF; /* Keeps text color white for high contrast */
  font-weight: bold; /* Ensure the text is bold */
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.7); /* Optional: Adds a slight shadow for depth */
}

.feedback-correct {
  background-color: #4CAF50; /* Green background */
  border: 2px solid #FFF; /* White border for contrast */
  color: fff;
}

.feedback-incorrect {
  background-color: #F44336; /* Red background */
  border: 2px solid #FFF; /* White border for contrast */
}
.quiz-title, .question-counter, .question, .feedback {
  text-align: center;
  font-size: 1rem;
}

.results-summary {
  padding: 20px; /* Adds padding inside the summary box */
  background-color: rgba(255, 255, 255, 0.9); /* Semi-transparent white background */
  border-radius: 15px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  margin-top: 20px; /* Space above the summary */
  border: 2px solid #DDD; /* Light border for definition */
}

.results-summary h4 {
  color: #333; /* Darker text for contrast against light background */
  font-size: 1.5rem; /* Larger font size for the title */
}

.results-summary p {
  color: #555; /* Subtle text color for readability */
  font-size: 1rem; /* Larger font size for the content */
}

.options-list {
  list-style-type: none;
  padding: 0;
}

.option {
  margin: 10px 0;
  padding: 10px;
  color: black;
  background-color: #f4f4f8;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 1rem;
}

.option:hover {
  background-color: #d0ccd3;
}

.option.selected.correct {
  border-color: green;
}

.option.selected.incorrect {
  border-color: red;
}
.button-container {
  display: flex;
  justify-content: center; /* Center the buttons horizontally */
  margin-top: 20px; /* Space above the button container */
}
.action-button {
  background-color: #4a148C; 
  color: #DDD;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 25px;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
  background-color: #36454f; /* Black background */
  color: white;
  justify-content: center; 
}


.action-button:hover {
  background-color: black; /* Dark purple background on hover */
}


.next-quiz-button {
  background-color: #4CAF50; /* Example green background */
  color: white;
  /* Add other styles as needed */
}

.centered-title {
  text-align: center;
  font-size: 2rem; /* Increase font size */
  color: #36454f; /* Dark text for contrast */
  margin-bottom: 20px; /* Add some space below the title */
}

.quiz-container h1 {
  font-size: 1.5rem; /* Make questions stand out */
  color: #4A148C; /* Use the dark purple for questions */
  margin-bottom: 15px;
}

.option-card {
  background-color: #f4f4f8;
  border: 1px solid #ccc;
  padding: 10px;
  margin: 5px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color .3s, transform .2s;
}

.option-card:hover {
  background-color: #e9e9ef;
  transform: scale(1.02); /* Slightly enlarge options on hover */
}

.feedback-icon {
  font-weight: bold;
  margin-left: 15px;
}

.correct {
  color: green;
}

.incorrect {
  color: red;
}

.feedback {
  margin-top: 20px;
  text-align: center;
}

</style>